import React, { useState } from 'react';
import { Modal } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload/interface';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input } from '@app/components/common/inputs/Input/Input';
import { Upload } from '@app/components/common/Upload/Upload';
import { Button } from '@app/components/common/buttons/Button/Button';
import { notificationController } from '@app/controllers/notificationController';
import { useResetFormOnCloseModal } from '@app/components/forms/ControlForm/useResetFormOnCloseModal';
import { createExample } from '@app/api/aiDrawExamples.api';

interface CreateExampleFormModalProps {
  open: boolean;
  onCancel: () => void;
  onFinish: () => void;
}

interface CreateExampleFormData {
  photo: UploadFile[];
  name: string;
}

export const CreateExampleFormModal: React.FC<CreateExampleFormModalProps> = ({ open, onCancel, onFinish }) => {
  const [form] = BaseForm.useForm();
  const [isLoading, setLoading] = useState(false);

  useResetFormOnCloseModal({
    form,
    open,
  });

  const normFile = (e = { fileList: [] }) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleSubmit = (values: CreateExampleFormData) => {
    const file = values.photo?.[0]?.originFileObj;
    if (!file) {
      notificationController.error({ message: 'Please select a photo' });
      return;
    }
    // No category/style: the client dropped those choices from the form, so the backend
    // defaults them (Object / Pencil).
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('name', values.name.trim());

    setLoading(true);
    createExample(formData)
      .then(() => {
        notificationController.success({
          message: 'Example generation started',
          description: 'This takes a little while — it will appear once ready.',
        });
        onFinish();
      })
      .catch(() => {
        // Error toast is already shown by the httpApi response interceptor.
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      title="Create Example"
      open={open}
      onOk={() => form.submit()}
      onCancel={onCancel}
      confirmLoading={isLoading}
      okText="Create"
      cancelText="Cancel"
    >
      <BaseForm form={form} layout="vertical" name="createExampleForm" onFinish={handleSubmit}>
        <BaseForm.Item
          name="photo"
          label="Photo"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          initialValue={[]}
          rules={[{ required: true, message: 'Photo is required' }]}
        >
          <Upload accept="image/*" maxCount={1} listType="picture" beforeUpload={() => false}>
            <Button type="default" icon={<UploadOutlined />}>
              Click to upload
            </Button>
          </Upload>
        </BaseForm.Item>
        <BaseForm.Item
          name="name"
          label="Name"
          extra="The name will be auto-translated to French, German, Spanish and Italian."
          rules={[{ required: true, whitespace: true, message: 'Name is required' }]}
        >
          <Input placeholder="Example name" />
        </BaseForm.Item>
      </BaseForm>
    </Modal>
  );
};
